import React, { useMemo, useState } from "react";
import { X } from "lucide-react";
import { categoryOptionValue } from "../emptyCategories";

/**
 * Pick an existing list (or create a new one) to file the selected records under,
 * and say whether that is an ADD or a MOVE.
 *
 * Both are real gestures and the page used to guess. It guessed "move" until
 * 1 Sep 2026 (a tab silently dropped from 161 rows to 88), then "add" - and
 * then rows moved to "Find Data" stayed in the tab they came from as well
 * (Paul Jimoulis, CRM-2026-00024, 4 Sep 2026). So now it asks, and names the
 * tab that is being left.
 */
// `allowCreate` is false on a Database shared with the agent: rows can be moved
// between the owner's existing lists, but the tab structure stays the owner's.
// `allowOnly` is false there too - "this list only" clears memberships the
// caller may not be able to see.
export default function MoveToListModal({
  count,
  lists,
  // New categories with nothing in them yet - picking one makes the heading's
  // own list (CRM-2026-00099). Resolved by the page in onConfirm.
  emptyCategories = [],
  onClose,
  onConfirm,
  allowCreate = true,
  allowOnly = true,
  sourceList = null,
}) {
  // Never default to the tab the rows are already in - that is the one list
  // this dialog can't move them into.
  const firstOther = lists.find((l) => String(l.id) !== String(sourceList?.id)) || lists[0];
  const [choice, setChoice] = useState(
    firstOther?.id ? String(firstOther.id) : emptyCategories[0] ? categoryOptionValue(emptyCategories[0]) : ""
  );
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);
  // A move needs a tab to move OUT of, so on "All" there is nothing to leave
  // and adding is the only thing this can mean.
  const [mode, setMode] = useState(sourceList ? "move" : "add");

  // Every suburb has its own "2 Bedroom Unit" list, so the group has to show in
  // the picker or the two options read identically.
  const grouped = useMemo(() => {
    const groups = [];
    const byTitle = new Map();
    lists.forEach((l) => {
      const title = (l.group_name || "").trim();
      if (!byTitle.has(title)) {
        const g = { title, lists: [] };
        byTitle.set(title, g);
        groups.push(g);
      }
      byTitle.get(title).lists.push(l);
    });
    return groups.sort((a, b) => (a.title ? 1 : 0) - (b.title ? 1 : 0) || a.title.localeCompare(b.title));
  }, [lists]);

  // A brand-new list has nothing in it, so "move out of the current tab" is the
  // only sensible reading of Move; "this list only" is a repair for rows that
  // are already double-filed and means nothing on a list being created now.
  const creating = !!newName.trim();
  const targetIsSource = !!sourceList && !creating && String(choice) === String(sourceList.id);
  // The picked mode isn't always possible. Rather than let the button do
  // nothing, fall back to the nearest thing that is and label it honestly.
  let effectiveMode = mode;
  if (creating && mode === "only") effectiveMode = "move";
  if (!sourceList && effectiveMode === "move") effectiveMode = "add";
  if (targetIsSource && effectiveMode === "move") effectiveMode = allowOnly ? "only" : "add";
  if (!allowOnly && effectiveMode === "only") effectiveMode = "add";

  const confirm = async () => {
    if (!choice && !creating) return;
    setBusy(true);
    try {
      await onConfirm({
        listId: choice || null,
        newName: newName.trim() || null,
        mode: effectiveMode,
        fromListId: effectiveMode === "move" ? sourceList?.id ?? null : null,
      });
      onClose();
    } finally {
      setBusy(false);
    }
  };

  const sourceName = sourceList?.name || "";
  const options = [
    {
      key: "add",
      label: sourceName ? `Add to the list, and keep it in "${sourceName}" too` : "Add to the list",
      hint: "Use this when the same property belongs in both tabs.",
    },
    {
      key: "move",
      label: sourceName ? `Move it - take it out of "${sourceName}"` : "Move it",
      hint: !sourceList
        ? "Open a tab first - there is nothing to move these out of on All."
        : targetIsSource
        ? `"${sourceName}" is the tab these rows are already in - pick another list.`
        : `The rows leave "${sourceName}" and only show under the list you pick.`,
      disabled: !sourceList || targetIsSource,
    },
    {
      key: "only",
      label: "Put it in this list only - remove it from every other list",
      hint: "Tidies up rows that were added to a second tab before Move existed.",
      disabled: !allowOnly || creating,
    },
  ];

  return (
    <div className="fixed inset-0 bg-black/40 z-[80] flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-[420px] max-w-full flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="font-bold text-gray-900">
            {effectiveMode === "move" ? "Move" : "Add"} {count} {count === 1 ? "record" : "records"}
          </h3>
          <button onClick={onClose} className="text-gray-500">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-3">
          <label className="block">
            <span className="text-[11px] uppercase tracking-wide text-gray-400 font-bold">Choose a list</span>
            <select
              value={choice}
              onChange={(e) => {
                setChoice(e.target.value);
                setNewName("");
              }}
              className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
            >
              {!lists.length && !emptyCategories.length && <option value="">No lists yet</option>}
              {grouped.map((g) =>
                g.title ? (
                  <optgroup key={g.title} label={g.title}>
                    {g.lists.map((l) => (
                      <option key={l.id} value={l.id}>
                        {/* A category used as a list itself carries the
                            heading's own name, so say which one that is
                            rather than showing "Preston / Preston". */}
                        {l.name.trim().toLowerCase() === g.title.trim().toLowerCase()
                          ? `${l.name} (the heading itself)`
                          : l.name}
                      </option>
                    ))}
                  </optgroup>
                ) : (
                  g.lists.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name}
                    </option>
                  ))
                )
              )}
              {emptyCategories.length > 0 && (
                <optgroup label="New categories (nothing in them yet)">
                  {emptyCategories.map((t) => (
                    <option key={`cat-${t}`} value={categoryOptionValue(t)}>
                      {t}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
          </label>

          <div className="pt-1 space-y-1.5">
            <span className="text-[11px] uppercase tracking-wide text-gray-400 font-bold">
              What should happen to the rows
            </span>
            {options.map((o) => (
              <label
                key={o.key}
                className={`flex items-start gap-2 rounded-lg border px-3 py-2 cursor-pointer ${
                  effectiveMode === o.key ? "border-[#55c5d0] bg-[#55c5d0]/5" : "border-gray-200"
                } ${o.disabled ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                <input
                  type="radio"
                  name="movemode"
                  className="mt-0.5"
                  checked={effectiveMode === o.key}
                  disabled={o.disabled}
                  onChange={() => setMode(o.key)}
                />
                <span>
                  <span className="block text-sm font-semibold text-gray-800">{o.label}</span>
                  <span className="block text-[11px] text-gray-500">{o.hint}</span>
                </span>
              </label>
            ))}
          </div>

          {allowCreate ? (
            <>
              <div className="text-xs text-gray-400 text-center">— or —</div>

              <label className="block">
                <span className="text-[11px] uppercase tracking-wide text-gray-400 font-bold">Create a new list</span>
                <input
                  value={newName}
                  onChange={(e) => {
                    setNewName(e.target.value);
                    if (e.target.value) setChoice("");
                  }}
                  placeholder="New list name"
                  className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#55c5d0]"
                />
              </label>
            </>
          ) : (
            <p className="text-[11px] text-gray-400">
              New lists can only be created by the agent who owns this database.
            </p>
          )}
        </div>

        <div className="flex items-center justify-between px-5 py-4 border-t border-gray-200">
          <button onClick={onClose} className="text-sm font-semibold text-gray-600">
            Cancel
          </button>
          <button
            onClick={confirm}
            disabled={busy || (!choice && !creating)}
            className="px-4 py-2 rounded-lg bg-[#55c5d0] text-white text-sm font-bold disabled:opacity-50"
          >
            {busy
              ? effectiveMode === "move"
                ? "Moving…"
                : "Saving…"
              : effectiveMode === "move"
              ? "Move"
              : effectiveMode === "only"
              ? "File here only"
              : "Add"}
          </button>
        </div>
      </div>
    </div>
  );
}
